"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  fetchDocuments,
  deleteDocument,
  downloadDocument,
  clearError,
  viewDocument,
} from "../store/slices/documentSlice"
import AddDocument from "./AddDocument"
import { File, Plus, Eye, Edit, Trash2, X, Download, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react"

// --- Aesthetic Configuration ---
const PRIMARY_COLOR = "blue-600"
const PRIMARY_HOVER = "blue-700"
const SUCCESS_COLOR = "green-600"
const DANGER_COLOR = "red-600"

const DocumentsComponent = () => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { documents: allDocuments, loading, error } = useSelector((state) => state.documents)

  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [message, setMessage] = useState({ text: "", type: "" })
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)
  const [selectedStudentDocs, setSelectedStudentDocs] = useState([])
  const [selectedStudentInfo, setSelectedStudentInfo] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  useEffect(() => {
    dispatch(fetchDocuments())
  }, [dispatch])

  useEffect(() => {
    if (error) {
      setMessage({ text: `Error: ${error}`, type: "error" })
      dispatch(clearError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  // Group documents by student or show only the current student's documents
  const studentDocuments =
    user?.role === "student" ? allDocuments.filter((doc) => doc.student_id === user?.id) : allDocuments

  const groupedDocuments = studentDocuments.reduce((acc, doc) => {
    const studentId = doc.student_id
    const key = studentId || "N/A"
    if (!acc[key]) {
      acc[key] = {
        student_id: studentId || "N/A",
        student_name: doc.student_name || `Unknown Student (ID: ${studentId || "N/A"})`,
        student_code: doc.student_code || "-",
        documents: [],
      }
    }
    acc[key].documents.push(doc)
    return acc
  }, {})

  // Filter grouped documents based on search term
  const filteredGroupedDocuments = Object.values(groupedDocuments).filter(
    (studentData) =>
      studentData.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studentData.student_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      studentData.student_id?.toString().includes(searchTerm),
  )

  // Pagination logic
  const totalPages = Math.ceil(filteredGroupedDocuments.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedTableContent = filteredGroupedDocuments.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value))
    setCurrentPage(1) // Reset to first page when items per page changes
  }

  // --- Handlers ---
  const handleEdit = (doc) => {
    setEditingDoc(doc)
    setShowEditModal(true)
  }

  const handleViewAllDocuments = (studentData) => {
    setSelectedStudentDocs(studentData.documents)
    setSelectedStudentInfo({
      student_id: studentData.student_id,
      student_name: studentData.student_name,
      student_code: studentData.student_code,
    })
    setShowDocumentsModal(true)
  }

  const handleViewDocument = async (doc) => {
    try {
      const payload = await dispatch(viewDocument(doc.doc_id)).unwrap()
      const blob = payload?.blob
      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob)
        window.open(url, "_blank")
        window.URL.revokeObjectURL(url)
      } else {
        setMessage({ text: "View failed: Invalid file response received.", type: "error" })
      }
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message || "View failed"
      setMessage({ text: `View failed: ${msg}`, type: "error" })
      console.error("[v0] View Document Error:", error)
    }
  }

  const handleDownloadDocument = async (doc) => {
    try {
      const payload = await dispatch(downloadDocument(doc.doc_id)).unwrap()
      const blob = payload?.blob
      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = doc.doc_name || `document_${doc.doc_id}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        setMessage({ text: `Document ${doc.doc_id} downloaded successfully!`, type: "success" })
      } else {
        setMessage({ text: "Download failed: Invalid file response received.", type: "error" })
      }
    } catch (error) {
      const msg = typeof error === "string" ? error : error?.message || "Download failed"
      setMessage({ text: `Download failed: ${msg}`, type: "error" })
      console.error("[v0] Download Document Error:", error)
    }
  }

  const handleDelete = (docId) => {
    if (window.confirm("Are you sure you want to delete this document? This cannot be undone.")) {
      dispatch(deleteDocument(docId))
        .unwrap()
        .then(() => {
          dispatch(fetchDocuments())
          setMessage({ text: "Document deleted successfully!", type: "success" })
          if (showDocumentsModal) setShowDocumentsModal(false)
        })
        .catch((err) => {
          setMessage({ text: `Error deleting document: ${err.message || err}`, type: "error" })
        })
    }
  }

  // --- Render Functions ---
  if (loading) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 text-${PRIMARY_COLOR}`}>
        <Loader2 className={`animate-spin h-10 w-10 text-${PRIMARY_COLOR}`} />
        <span className="ml-2 text-gray-600 mt-2 text-lg">Loading documents...</span>
      </div>
    )
  }

  const tableContent = paginatedTableContent
  const isStudentView = user?.role === "student"

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
      {/* Header and Action Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b pb-4">
        <div className="flex items-center gap-3">
          <File className={`w-8 h-8 text-${PRIMARY_COLOR}`} />
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {isStudentView ? "My Documents" : "Documents Management"}
          </h2>
        </div>

        {(user?.role === "admin" ||
          user?.role === "super_admin" ||
          user?.role === "faculty" ||
          user?.role === "account" ||
          user?.role === "student" ||
          user?.role === "hod") && (
          <button
            onClick={() => setShowAddModal(true)}
            className={`flex items-center gap-2 bg-${PRIMARY_COLOR} hover:bg-${PRIMARY_HOVER} text-white font-semibold py-2 px-5 rounded-xl shadow-md transition-all transform hover:scale-[1.02]`}
          >
            <Plus className="w-5 h-5" />
            Upload New Document
          </button>
        )}
      </div>

      {/* Message Alerts */}
      {message.text && (
        <div
          className={`p-4 rounded-xl shadow-md border-l-4 ${
            message.type === "success"
              ? `border-green-500 bg-green-50 text-green-800`
              : `border-red-500 bg-red-50 text-red-800`
          } font-medium`}
        >
          <p>{message.text}</p>
        </div>
      )}

      {/* Search Bar */}
      {(!isStudentView || (isStudentView && Object.values(groupedDocuments).length > 0)) && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 mb-6">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by student name, code, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
        {paginatedTableContent.length === 0 ? (
          <div className="text-center py-12">
            <File className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-xl font-semibold text-gray-600">No documents found.</p>
            {!isStudentView && <p className="text-gray-500 mt-1">Try adjusting your search or add new documents.</p>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    {!isStudentView && (
                      <>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                          Student ID
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[180px]">
                          Student Name
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                          Student Code
                        </th>
                      </>
                    )}
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[120px]">
                      Document Count
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 min-w-[100px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedTableContent.map((studentData) => (
                    <tr key={studentData.student_id} className="hover:bg-blue-50/50 transition-colors">
                      {!isStudentView && (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {studentData.student_id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {studentData.student_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {studentData.student_code}
                          </td>
                        </>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 rounded-full shadow-sm">
                          {studentData.documents.length} document{studentData.documents.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleViewAllDocuments(studentData)}
                          className={`p-2 text-${PRIMARY_COLOR} hover:bg-blue-100 rounded-full transition-colors group`}
                          title="View All Documents"
                        >
                          <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                <span className="text-sm text-gray-600">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border border-gray-300 rounded-lg p-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-600">per page</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 rounded-lg text-sm ${
                        currentPage === page
                          ? `bg-${PRIMARY_COLOR} text-white`
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : `bg-${PRIMARY_COLOR} text-white hover:bg-${PRIMARY_HOVER}`
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* View All Documents Modal */}
      {showDocumentsModal && selectedStudentDocs && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Document List</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Student: **{selectedStudentInfo?.student_name}** | ID: {selectedStudentInfo?.student_id}
                </p>
              </div>
              <button
                onClick={() => setShowDocumentsModal(false)}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {selectedStudentDocs.map((doc) => (
                <div
                  key={doc.doc_id}
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <div className="flex-1 min-w-0 pr-4 mb-3 sm:mb-0">
                    <h4 className="font-semibold text-gray-900 truncate">
                      {doc.doc_name || `Document ID: ${doc.doc_id}`}
                    </h4>
                    <p className="text-sm text-gray-500">Type: **{doc.document_type || "General File"}**</p>
                    <p className="text-xs text-gray-400">
                      Uploaded: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "N/A"}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <button
                      onClick={() => handleViewDocument(doc)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                      title="View Document"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDownloadDocument(doc)}
                      className="p-2 text-green-600 hover:bg-green-100 rounded-full transition-colors"
                      title="Download Document"
                    >
                      <Download className="w-5 h-5" />
                    </button>

                    {(user?.role === "admin" ||
                      user?.role === "super_admin" ||
                      user?.role === "account" ||
                      user?.role === "student" ||
                      user?.role === "hod") && (
                      <button
                        onClick={() => {
                          setShowDocumentsModal(false)
                          handleEdit(doc)
                        }}
                        className="p-2 text-orange-600 hover:bg-orange-100 rounded-full transition-colors"
                        title="Edit Document"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                    )}
                    {(user?.role === "admin" || user?.role === "super_admin" || user?.role === "account") && (
                      <button
                        onClick={() => {
                          handleDelete(doc.doc_id)
                        }}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                        title="Delete Document"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {selectedStudentDocs.length === 0 && (
                <div className="text-center py-8 border-dashed border-2 rounded-xl text-gray-500">
                  <File className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p>No documents found for this student.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Modal Overlay */}
      {showAddModal &&
        (user?.role === "admin" ||
          user?.role === "super_admin" ||
          user?.role === "faculty" ||
          user?.role === "account" ||
          user?.role === "student" ||
          user?.role === "hod") && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Upload Document</h3>
              <AddDocument setShowAddModal={setShowAddModal} />
            </div>
          </div>
        )}

      {/* Edit Modal Overlay */}
      {showEditModal &&
        (user?.role === "admin" ||
          user?.role === "super_admin" ||
          user?.role === "account" ||
          user?.role === "student" ||
          user?.role === "hod") &&
        editingDoc && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1300]">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
                Edit Document (ID: {editingDoc.doc_id})
              </h3>
              <AddDocument setShowAddModal={setShowEditModal} editingDoc={editingDoc} />
            </div>
          </div>
        )}
    </div>
  )
}

export default DocumentsComponent
